import { useState, useEffect } from "react";
import { Save, RotateCcw, Settings } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";

const FIELD_LABELS = {
  manufacturer: "Nome produttore",
  trademark: "Marchio registrato",
  address: "Indirizzo postale",
  website: "Indirizzo web",
  email: "Indirizzo email",
  model: "Identificazione modello",
  place_manufacture: "Luogo di fabbricazione",
  weight: "Peso",
  capacity: "Capacita",
  chemistry: "Composizione chimica",
  hazardous: "Sostanze pericolose",
  extinguishing: "Agente estinguente",
  critical_materials: "Materie prime critiche",
};

const TechnicalInfoAdmin = () => {
  const { token } = useAuth();
  const [batteryData, setBatteryData] = useState({});
  const [conformity, setConformity] = useState({ content: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/technical-info`);
        setBatteryData(res.data.battery_data || {});
        setConformity(res.data.conformity || { content: "" });
      } catch (err) {
        toast.error("Errore caricamento dati tecnici");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/technical-info`, {
        battery_data: batteryData,
        conformity,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Dati tecnici aggiornati con successo!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/technical-info`);
      setBatteryData(res.data.battery_data || {});
      setConformity(res.data.conformity || { content: "" });
      toast.success("Dati ricaricati");
    } catch (err) {
      toast.error("Errore ricaricamento");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-lojack-black flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-lojack-black" data-testid="technical-info-admin">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-lojack-red/10 border border-lojack-red/30 flex items-center justify-center">
              <Settings className="w-5 h-5 text-lojack-red" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white" data-testid="tech-admin-title">Gestione Info Tecniche</h1>
              <p className="text-gray-500 text-sm">Modifica i dati della pagina Technical Info pubblica</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleReset} className="btn-lojack-outline flex items-center gap-2 text-sm" data-testid="tech-admin-reset">
              <RotateCcw size={16} /> Ricarica
            </button>
            <button onClick={handleSave} disabled={saving} className="btn-lojack flex items-center gap-2 text-sm disabled:opacity-50" data-testid="tech-admin-save">
              <Save size={16} /> {saving ? "Salvataggio..." : "Salva Modifiche"}
            </button>
          </div>
        </div>

        {/* Battery Data Section */}
        <div className="bg-[#111] border border-gray-800 rounded-2xl overflow-hidden mb-6">
          <div className="px-5 py-3 border-b border-gray-800 bg-[#0D0D0D]">
            <h2 className="text-sm font-semibold text-lojack-red uppercase tracking-wider">Dati Batteria</h2>
          </div>
          <div className="p-5 space-y-4">
            {Object.entries(FIELD_LABELS).map(([key, label]) => (
              <div key={key}>
                <label className="text-gray-400 text-xs block mb-1">{label}</label>
                <input
                  value={batteryData[key] || ""}
                  onChange={(e) => setBatteryData({ ...batteryData, [key]: e.target.value })}
                  className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-xl text-white text-sm focus:border-lojack-red focus:outline-none transition-colors"
                  placeholder={`Inserisci ${label.toLowerCase()}`}
                  data-testid={`tech-field-${key}`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Conformity Section */}
        <div className="bg-[#111] border border-gray-800 rounded-2xl overflow-hidden mb-6">
          <div className="px-5 py-3 border-b border-gray-800 bg-[#0D0D0D]">
            <h2 className="text-sm font-semibold text-blue-400 uppercase tracking-wider">Dichiarazione di Conformita</h2>
          </div>
          <div className="p-5">
            <label className="text-gray-400 text-xs block mb-1">Contenuto (lascia vuoto per mostrare il placeholder "in fase di definizione")</label>
            <textarea
              value={conformity.content || ""}
              onChange={(e) => setConformity({ ...conformity, content: e.target.value })}
              rows={6}
              className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-xl text-white text-sm focus:border-lojack-red focus:outline-none transition-colors resize-y"
              placeholder="Inserisci il testo della dichiarazione di conformita..."
              data-testid="tech-field-conformity"
            />
          </div>
        </div>

        {/* Preview Link */}
        <div className="bg-[#111] border border-gray-800 rounded-2xl p-5">
          <p className="text-gray-400 text-sm">
            Anteprima pubblica:{" "}
            <a href="/technical-info/ATU1650MB" target="_blank" rel="noopener noreferrer" className="text-lojack-red hover:underline" data-testid="tech-admin-preview-link">
              /technical-info/ATU1650MB
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TechnicalInfoAdmin;
